// IndexedDB "retrowser": ROMs (keyed by SHA-1, only when the player asks), saves (keyed by game id + native kind),
// files the game handed to the player (rtw_export_file, per game id), settings. Nothing here ever leaves the device.

export interface StoredRom { sha1: string; name: string; size: number; platform: string; blob: Blob; addedAt: number }
export interface StoredSave { key: string; id: string; kind: string; bytes: Uint8Array; generation: number; savedAt: number }
export interface StoredFile { id: number; game: string; name: string; mime: string; bytes: Uint8Array; at: number }

const DB = 'retrowser';
const VERSION = 2; // 2: + files
let dbp: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (dbp) return dbp;
  dbp = new Promise((ok, bad) => {
    const r = indexedDB.open(DB, VERSION);
    r.onupgradeneeded = () => {
      const db = r.result;
      if (!db.objectStoreNames.contains('roms')) db.createObjectStore('roms', { keyPath: 'sha1' }).createIndex('platform', 'platform');
      if (!db.objectStoreNames.contains('saves')) db.createObjectStore('saves', { keyPath: 'key' });
      if (!db.objectStoreNames.contains('settings')) db.createObjectStore('settings');
      if (!db.objectStoreNames.contains('files')) db.createObjectStore('files', { keyPath: 'id', autoIncrement: true }).createIndex('game', 'game');
    };
    r.onsuccess = () => ok(r.result);
    r.onerror = () => { dbp = null; bad(r.error); };
  });
  return dbp;
}

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((ok, bad) => { r.onsuccess = () => ok(r.result); r.onerror = () => bad(r.error); });
}

async function store(name: 'roms' | 'saves' | 'settings' | 'files', mode: IDBTransactionMode): Promise<IDBObjectStore> {
  return (await open()).transaction(name, mode).objectStore(name);
}

export async function putRom(rom: Omit<StoredRom, 'addedAt' | 'size' | 'blob'> & { bytes: Uint8Array }): Promise<void> {
  const v: StoredRom = { sha1: rom.sha1, name: rom.name, platform: rom.platform, size: rom.bytes.length,
    blob: new Blob([rom.bytes as Uint8Array<ArrayBuffer>]), addedAt: Date.now() };
  await req((await store('roms', 'readwrite')).put(v));
}

export async function listRoms(platform?: string): Promise<StoredRom[]> {
  const s = await store('roms', 'readonly');
  const all = await req(platform ? s.index('platform').getAll(platform) : s.getAll()) as StoredRom[];
  return all.sort((a, b) => b.addedAt - a.addedAt);
}

export async function deleteRom(sha1: string): Promise<void> {
  await req((await store('roms', 'readwrite')).delete(sha1));
}

export async function romBytes(r: StoredRom): Promise<Uint8Array> {
  return new Uint8Array(await r.blob.arrayBuffer());
}

export const saveKey = (id: string, kind: string) => `${id}:${kind}`;

export async function getSave(id: string, kind: string): Promise<StoredSave | undefined> {
  return await req((await store('saves', 'readonly')).get(saveKey(id, kind))) as StoredSave | undefined;
}

export async function putSave(id: string, kind: string, bytes: Uint8Array, generation: number): Promise<void> {
  const v: StoredSave = { key: saveKey(id, kind), id, kind, bytes, generation, savedAt: Date.now() };
  await req((await store('saves', 'readwrite')).put(v));
}

/** Files from rtw_export_file for one game, oldest first. */
export async function listFiles(game: string): Promise<StoredFile[]> {
  const all = await req((await store('files', 'readonly')).index('game').getAll(game)) as StoredFile[];
  return all.sort((a, b) => a.at - b.at || a.id - b.id);
}

/** Store a file; then drop the oldest ones of that game while it is over the cap. Returns the names dropped. */
export async function addFile(f: Omit<StoredFile, 'id'>, cap: { bytes: number; count: number }): Promise<{ id: number; dropped: string[] }> {
  const id = await req((await store('files', 'readwrite')).add(f)) as number;
  const drop = evictOldest(await listFiles(f.game), cap, id);
  if (drop.length) {
    const s = await store('files', 'readwrite');
    await Promise.all(drop.map((x) => req(s.delete(x.id))));
  }
  return { id, dropped: drop.map((x) => x.name) };
}

export async function deleteFile(id: number): Promise<void> {
  await req((await store('files', 'readwrite')).delete(id));
}

/** Which of `files` (oldest first) to drop so the rest fit the cap. The file `keep` (just added) is never dropped. */
export function evictOldest<T extends { id: number; bytes: { length: number } }>(files: T[], cap: { bytes: number; count: number }, keep?: number): T[] {
  let total = files.reduce((a, f) => a + f.bytes.length, 0);
  let count = files.length;
  const out: T[] = [];
  for (const f of files) {
    if (total <= cap.bytes && count <= cap.count) break;
    if (f.id === keep) continue;
    out.push(f);
    total -= f.bytes.length;
    count--;
  }
  return out;
}

export function idbAvailable(): boolean {
  try { return typeof indexedDB !== 'undefined'; } catch { return false; }
}
